import {
  Collapse,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerProps,
} from '@chakra-ui/react'
import { Button, LightModeBox, SingleDatePicker, Switch, ToggleButton } from 'components'
import { useAlert, useCurrentUser, useWorkspace } from 'components/providers'
import dayjs from 'dayjs'
import { isEqual } from 'lodash'
import { useEffect, useState } from 'react'
import { WorkspaceSubscription } from 'types'
import { createTimestamp, updateDoc } from 'utils'

interface Props extends Omit<DrawerProps, 'children'> {}

export default function ManageSubscriptionDrawer({ isOpen, onClose, ...rest }: Props) {
  const { confirm } = useAlert()
  const user = useCurrentUser()
  const { workspace } = useWorkspace()

  const initialData = { ...defaultSubscription, ...workspace?.subscription }
  const [draft, setDraft] = useState(initialData)
  const { billingFrequency, isTrial, limitsResetDate, nextPaymentDueDate, plan, selectedResource } =
    draft
  const [matchDates, setMatchDates] = useState(limitsResetDate === nextPaymentDueDate)
  const dirty = !isEqual(initialData, draft)
  const lockDates = matchDates && billingFrequency === 'yearly'

  useEffect(() => {
    if (!isOpen) return

    setDraft(initialData)
    setMatchDates(initialData.limitsResetDate === initialData.nextPaymentDueDate)
  }, [isOpen, workspace])

  useEffect(() => {
    if (matchDates && nextPaymentDueDate !== limitsResetDate) setMatchDates(false)
  }, [nextPaymentDueDate, limitsResetDate])

  async function handleDone() {
    if (!dirty) return onClose()

    const { isTrial, selectedResource, ...requiredProps } = draft
    const subscription = {
      ...requiredProps,
      ...(plan === 'basic' && { selectedResource }),
      ...(isTrial && { isTrial: true }),
    }
    await updateDoc(user!.id, 'Workspace', { id: workspace!.id, subscription })
    onClose()
  }

  async function handleCancel() {
    if (dirty) {
      const response = await confirm('Discard changes?', {
        cancelText: 'Cancel',
        okText: 'Discard',
      })
      if (!response) return
    }

    onClose()
  }

  return (
    <Drawer isOpen={isOpen} onClose={handleCancel} {...rest}>
      <DrawerOverlay />
      <LightModeBox>
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Manage Settings</DrawerHeader>

          <DrawerBody alignItems="flex-start" display="flex" flexDirection="column" gap={4}>
            <ToggleButton
              label="Billing Frequency"
              value={billingFrequency}
              options={[
                { label: 'Month', value: 'monthly' },
                { label: 'Annual', value: 'yearly' },
              ]}
              onChange={billingFrequency => setDraft({ ...draft, billingFrequency })}
            />
            <Switch
              label="Trial plan"
              isChecked={isTrial}
              onChange={isTrial => setDraft({ ...draft, isTrial })}
            />
            <ToggleButton
              label="Plan"
              value={plan}
              options={[
                { label: 'Basic', value: 'basic' },
                { label: 'Pro', value: 'pro' },
                { label: 'Enterprise', value: 'enterprise' },
              ]}
              onChange={plan => setDraft({ ...draft, plan })}
            />
            <Collapse in={plan === 'basic'}>
              <ToggleButton
                label="Selected Resource"
                value={selectedResource}
                options={[
                  { label: 'Events', value: 'event' },
                  { label: 'Facilities', value: 'facility' },
                ]}
                onChange={selectedResource => setDraft({ ...draft, selectedResource })}
              />
            </Collapse>
            <SingleDatePicker
              date={nextPaymentDueDate ? new Date(nextPaymentDueDate) : undefined}
              label="Next Payment Due Date"
              handleDateChange={v => {
                setDraft({
                  ...draft,
                  nextPaymentDueDate: v.getTime(),
                  ...(lockDates && { limitsResetDate: v.getTime() }),
                })
              }}
            />
            <Collapse in={billingFrequency === 'yearly'}>
              <Switch
                label="Reset limits on due date"
                isChecked={matchDates}
                onChange={checked => {
                  setMatchDates(checked)
                  if (checked) setDraft({ ...draft, limitsResetDate: nextPaymentDueDate })
                }}
              />
            </Collapse>
            <Collapse in={!lockDates}>
              <SingleDatePicker
                date={limitsResetDate ? new Date(limitsResetDate) : undefined}
                label="Limits Reset Date"
                handleDateChange={v => setDraft({ ...draft, limitsResetDate: v.getTime() })}
              />
            </Collapse>
          </DrawerBody>

          <DrawerFooter>
            {dirty && (
              <Button variant="ghost" onClick={handleCancel}>
                Cancel
              </Button>
            )}
            <Button palette={dirty ? 'primary' : undefined} onClick={handleDone}>
              {dirty ? 'Save' : 'Done'}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </LightModeBox>
    </Drawer>
  )
}

const today = createTimestamp()

const defaultSubscription: Required<WorkspaceSubscription> = {
  billingFrequency: 'yearly',
  isTrial: false,
  limitsResetDate: today,
  nextPaymentDueDate: dayjs(today).add(1, 'year').unix() * 1000,
  plan: 'enterprise',
  selectedResource: 'event',
}
